import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Button, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Draw-only signature pad for the consent form. Unlike the editProfile
// SignatureCanvas (bound to a single react-hook-form field with an
// image-upload mode), this is a controlled component: every completed
// stroke commits the canvas as a PNG data URL via onChange, so the
// patient / witness / interpreter pads can coexist on one screen with
// no extra "confirm" click per signature.
const ConsentSignaturePad = ({ label, value, onChange }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [drawing, setDrawing] = useState(false);

    // --- Responsive canvas sizing (preserves strokes on resize) ---
    const resizeCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        const width = Math.min(container.offsetWidth, 500);
        const height = Math.round(width / 2.5);
        if (canvas.width === width && canvas.height === height) return;

        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = canvas.width;
        tempCanvas.height = canvas.height;
        tempCanvas.getContext('2d').drawImage(canvas, 0, 0);

        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(tempCanvas, 0, 0, tempCanvas.width, tempCanvas.height, 0, 0, width, height);
    }, []);

    useEffect(() => {
        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);
        return () => window.removeEventListener('resize', resizeCanvas);
    }, [resizeCanvas]);

    const getCoordinates = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        if (e.touches && e.touches.length > 0) {
            return {
                x: e.touches[0].clientX - rect.left,
                y: e.touches[0].clientY - rect.top
            };
        }
        return {
            x: e.nativeEvent.offsetX,
            y: e.nativeEvent.offsetY
        };
    };

    const startDrawing = (e) => {
        if (e.touches) e.preventDefault();
        const ctx = canvasRef.current.getContext('2d');
        const { x, y } = getCoordinates(e);
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.strokeStyle = '#000';
        ctx.beginPath();
        ctx.moveTo(x, y);
        setDrawing(true);
    };

    const draw = (e) => {
        if (!drawing) return;
        if (e.touches) e.preventDefault();
        const ctx = canvasRef.current.getContext('2d');
        const { x, y } = getCoordinates(e);
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const endDrawing = (e) => {
        if (!drawing) return;
        if (e.touches) e.preventDefault();
        setDrawing(false);
        // Commit the whole canvas after each stroke — no confirm step.
        onChange(canvasRef.current.toDataURL('image/png'));
    };

    const clear = () => {
        const canvas = canvasRef.current;
        canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
        onChange('');
    };

    return (
        <div ref={containerRef}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
                {Boolean(value) && <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />}
            </Box>
            <canvas
                style={{
                    border: value ? '2px solid #4caf50' : '1px solid #9CA8B6',
                    borderRadius: 4,
                    display: 'block',
                    touchAction: 'none',
                    maxWidth: '100%',
                    backgroundColor: '#fff',
                }}
                ref={canvasRef}
                onMouseDown={startDrawing}
                onMouseUp={endDrawing}
                onMouseMove={draw}
                onMouseLeave={endDrawing}
                onTouchStart={startDrawing}
                onTouchEnd={endDrawing}
                onTouchMove={draw}
            />
            <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button type="button" size="small" onClick={clear}>Clear</Button>
                <Typography variant="caption" color="text.secondary">
                    {value ? 'Signature captured.' : 'Sign above with finger, stylus or mouse.'}
                </Typography>
            </Box>
        </div>
    );
};

export default ConsentSignaturePad;
